/* screens/checkout/CheckoutSuccess.jsx — подтверждение оплаты + polling (S11).
   ФИКС №26: карточка деталей показывается СРАЗУ при наличии payment, независимо
   от фазы polling; polling-прогресс — отдельный блок. Таймаут → recoverable
   warning + CTA (не вечный спиннер). Источник: 12 §2.1, Сценарий 7.

   РОУТ ВНЕ RequireAuth. Счёт по именной ссылке вправе оплатить кто угодно —
   бухгалтер, учредитель, партнёр, — и после Робокассы он возвращается СЮДА. С
   гардом его выбрасывало на /signin, он читал это как «деньги не прошли» и
   платил второй раз. Ровно так платёж и терялся. Гость опознаётся по `?pl=`
   (токен ссылки, который Робокасса-редирект принёс с собой) и читает статус
   тем же контрактом, но через токен-скоуп. Без токена и без сессии страница
   сама уводит на вход — гард никуда не делся, он просто переехал внутрь.
   ХЕНДОФФ: → pages/checkout/CheckoutSuccess.jsx. */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { hooks } from '../../hooks/index.jsx';
import { useNotificationsStore } from '../../hooks/useNotificationsStore.jsx';
import { PLAN_LABELS } from '../../lib/adapters/checkout.js';
import { subscriptionReal } from '../../lib/adapters/subscription.js';
import { fmt } from '../../lib/format.js';
import { RR } from '../../lib/router.jsx';
import { CheckoutLayout } from './CheckoutLayout.jsx';
import { CheckoutRow } from './CheckoutRow.jsx';

  const { useState, useEffect, useRef } = React;
  const { useSearchParams, useNavigate } = RR;
  const { useFakeQuery } = hooks;

  const MAX_TICKS = 15;   // 15 × 2с = 30с
  const TICK_MS = 2000;

  function CheckoutSuccess() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const invoiceId = params.get('invoice_id');
    const ws = params.get('ws') || undefined;   // бизнес-ws счёта (не активного) → X-Workspace-Id для статуса
    // Токен именной ссылки, если оплата шла по ней (принёс редирект Робокассы).
    const plToken = params.get('pl') || '';
    const store = useNotificationsStore();
    const { user, switchWorkspace } = useAuth();
    // Аккаунта нет — значит перед нами посторонний плательщик. Статус читаем
    // токен-скоупом: сессионный `/payments/<id>/` ответил бы ему 404, то есть
    // «оплата не найдена» человеку, который только что заплатил.
    const guest = !!plToken && !user;
    const fetchStatus = React.useCallback(
      () => (guest
        ? subscriptionReal.getLinkPaymentStatus(plToken, invoiceId)
        : subscriptionReal.getPaymentStatus(invoiceId, ws)),
      [guest, plToken, invoiceId, ws],
    );
    // Пространство, созданное этой оплатой (режим «новое пространство» именной
    // ссылки). null у всех остальных платежей.
    const [createdWs, setCreatedWs] = useState(null);
    const switched = useRef(false);

    const { data: payment, loading, error } =
      useFakeQuery('pay-success-' + invoiceId + (ws ? ':' + ws : '') + (guest ? ':g' : ''),
        fetchStatus, [fetchStatus]);

    // фаза polling: 'polling' | 'succeeded' | 'timeout' | 'error'
    const [phase, setPhase] = useState('polling');
    const [paidAt, setPaidAt] = useState(null);
    const ticks = useRef(0);
    const notified = useRef(false);

    useEffect(() => {
      if (!invoiceId || loading) return;
      // жёсткая ошибка загрузки деталей (404/500) — сразу error-фаза с CTA, не вечный спиннер (фикс №26)
      if (error) { setPhase('error'); return; }
      // если платёж уже завершён к моменту загрузки — не крутим спиннер
      if (payment && payment.status === 'succeeded') {
        setPhase('succeeded'); setPaidAt(payment.paid_at);
        if (payment.created_workspace) setCreatedWs(payment.created_workspace);
      }
    }, [invoiceId, loading, error, payment]);

    useEffect(() => {
      if (!invoiceId || error || phase !== 'polling') return;
      ticks.current = 0;
      const tick = () => {
        ticks.current += 1;
        // polling — тот же запрос, что и первичная загрузка (сессионный либо
        // токен-скоупный: у гостя сессии нет).
        fetchStatus().then((st) => {
          if (st.status === 'succeeded') {
            setPaidAt(st.paid_at); setPhase('succeeded');
            // Пространство создаётся в вебхуке провайдера — то есть могло
            // появиться уже ПОСЛЕ первой загрузки деталей. Забираем его из
            // того же опроса, которым и так ждём подтверждения оплаты.
            if (st.created_workspace) setCreatedWs(st.created_workspace);
          }
          // на страницу отказа уводим только по ОКОНЧАТЕЛЬНОМУ отказу: статус,
          // поставленный догадкой (is_provisional), настоящий ResultURL ещё
          // отменит — иначе клиента с уже прошедшей оплатой швыряет на «не прошла»
          // Токен уносим и на страницу отказа: без него посторонний плательщик
          // упрётся там в тот же /signin, ради обхода которого всё и делалось.
          else if (st.status === 'failed' && !st.is_provisional) { navigate('/checkout/fail?invoice_id=' + encodeURIComponent(invoiceId) + (ws ? '&ws=' + encodeURIComponent(ws) : '') + (plToken ? '&pl=' + encodeURIComponent(plToken) : '')); }
          else if (ticks.current >= MAX_TICKS) { setPhase('timeout'); }
        }).catch(() => { if (ticks.current >= MAX_TICKS) setPhase('error'); });
      };
      const t = setInterval(tick, TICK_MS);
      const first = setTimeout(tick, 600); // быстрая первая проверка
      return () => { clearInterval(t); clearTimeout(first); };
    }, [invoiceId, ws, plToken, error, phase, navigate, fetchStatus]);

    // при успехе — синхронизируем стор уведомлений (бейдж колокола актуален).
    // У гостя колокола нет, а запрос ушёл бы в 403 — дёргаем только своим.
    useEffect(() => {
      if (guest) return;
      if (phase === 'succeeded' && !notified.current) { notified.current = true; store.refetch(); }
    }, [guest, phase, store]);

    // Купил заведение — в нём и работать. Переключаем РОВНО ОДИН раз (ref, а не
    // состояние: опрос тикает дальше и без флага дёргал бы switchWorkspace на
    // каждом тике). Сбой переключения не показываем ошибкой: деньги прошли,
    // пространство создано, а выбрать его можно и переключателем в шапке.
    useEffect(() => {
      if (guest || phase !== 'succeeded' || !createdWs || switched.current) return;
      switched.current = true;
      Promise.resolve(switchWorkspace(createdWs.id)).catch(() => { /* переживём */ });
    }, [guest, phase, createdWs, switchWorkspace]);

    const planLabel = (p) => p.plan_label || PLAN_LABELS[p.plan_slug] || p.plan_slug;

    // шапка по фазе
    const headIcon = phase === 'succeeded'
      ? <div className="checkout-icon checkout-icon--ok"><Icon name="check-circle" size={30} /></div>
      : phase === 'timeout' || phase === 'error'
        ? <div className="checkout-icon checkout-icon--warn"><Icon name="clock" size={28} /></div>
        : <div className="checkout-icon checkout-icon--spin"><Icon name="spinner" size={28} /></div>;

    const headTitle = phase === 'succeeded' ? 'Оплата подтверждена'
      : phase === 'timeout' ? 'Обработка задерживается'
      : phase === 'error' ? 'Не удалось проверить статус'
      : 'Проверяем оплату…';

    return (
      <CheckoutLayout>
        <header className="checkout-head checkout-head--center">
          {headIcon}
          <div className="t-eyebrow text-dim">Оплата подписки</div>
          <h1 className="t-h2">{headTitle}</h1>
        </header>

        {/* Детали — СРАЗУ при наличии payment (фикс №26), независимо от polling */}
        {loading && (
          <div className="checkout-state checkout-state--inline"><Icon name="spinner" size={22} /><span className="text-dim">Загружаем детали…</span></div>
        )}
        {error && (
          <div className="callout callout--bad checkout-banner"><Icon name="x-circle" size={18} /><span>Не удалось загрузить детали платежа.</span></div>
        )}
        {payment && (
          <div className="checkout-details">
            <CheckoutRow label="Тариф" value={planLabel(payment)} />
            <CheckoutRow label="Сумма" value={fmt.money(payment.amount_rub)} mono tone="muted" />
            <CheckoutRow label="Счёт" value={payment.invoice_id} mono />
            {phase === 'succeeded' && (
              <CheckoutRow label="Оплачено" value={fmt.date(paidAt || payment.paid_at || new Date())} tone="success" />
            )}
            {createdWs && <CheckoutRow label="Пространство" value={createdWs.name} tone="success" />}
          </div>
        )}

        {/* Прогресс — ОТДЕЛЬНЫЙ блок (не подменяет детали) */}
        <div className="checkout-progress" aria-live="polite">
          {phase === 'polling' && (
            <div className="checkout-progress__row text-dim"><Icon name="spinner" size={16} /><span>Проверяем статус платежа…</span></div>
          )}
          {phase === 'succeeded' && (
            <div className="checkout-progress__row text-ok"><Icon name="check" size={16} /><span>
              {/* Гостю не обещаем «подписка активна У ВАС»: доступ получил
                  клиент, а не плательщик, и спутать это — прямой путь к
                  вопросу «я заплатил, а где мой доступ». */}
              {guest
                ? 'Оплата подтверждена. Подписка подключена клиенту, на аккаунт которого выставлен счёт.'
                : (createdWs
                  ? `Оплата подтверждена. Пространство «${createdWs.name}» создано и уже выбрано.`
                  // Акция встала в очередь за текущим тарифом: «подписка активна»
                  // было бы неправдой — до этой даты работает прежний тариф.
                  : (payment && payment.needs_review === 'duplicate_promo'
                    ? 'Оплата получена, но этот тариф у вас уже оплачен — мы свяжемся с вами и вернём лишний платёж.'
                  : payment && payment.needs_review === 'underpaid_promo'
                    ? 'Оплата получена. Сумма меньше цены тарифа при вашей текущей подписке — мы свяжемся с вами, чтобы подключить его или вернуть деньги.'
                    : (payment && payment.queued_starts_at
                      ? `Оплата подтверждена. Тариф начнёт действовать ${fmt.date(payment.queued_starts_at)}, когда закончится текущий.`
                      : 'Оплата подтверждена, подписка активна.')))}
            </span></div>
          )}
          {phase === 'timeout' && (
            <div className="callout callout--warn checkout-banner">
              <Icon name="alert-circle" size={18} />
              <span>Обработка задерживается. Проверьте статус вручную или напишите в поддержку — деньги не потеряются.</span>
            </div>
          )}
          {phase === 'error' && (
            <div className="callout callout--bad checkout-banner">
              <Icon name="alert-triangle" size={18} />
              <span>Не удалось проверить статус. Обновите страницу или вернитесь к тарифам.</span>
            </div>
          )}
        </div>

        {/* Действия — при succeeded / timeout / error */}
        {(phase === 'succeeded' || phase === 'timeout' || phase === 'error') && (
          <div className="checkout-actions">
            {/* Гостю не предлагаем «Настройки» и «На главную»: за ними стоит
                RequireAuth, и обе кнопки увели бы его на /signin — то есть
                выглядели бы как «оплата всё-таки не засчиталась». Ему нужен
                ровно один путь — назад к счёту, где видно состояние оплаты. */}
            {!guest && (
              <Button variant="primary" className="full"
                onClick={() => navigate(createdWs ? '/kitchen' : '/settings?tab=subscription')}>
                {createdWs ? 'Перейти в новое пространство' : 'Перейти в Настройки'}
              </Button>
            )}
            {phase !== 'succeeded' && (
              <Button variant="secondary" className="full" iconLeft="refresh"
                onClick={() => { if (error) window.location.reload(); else setPhase('polling'); }}>Проверить ещё раз</Button>
            )}
            {guest
              ? (
                <Button variant={phase === 'succeeded' ? 'primary' : 'secondary'} className="full"
                  onClick={() => navigate('/pay/' + encodeURIComponent(plToken))}>
                  Вернуться к счёту
                </Button>
              )
              : (
                <a className="link checkout-link" href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>На главную</a>
              )}
          </div>
        )}
      </CheckoutLayout>
    );
  }

export { CheckoutSuccess };
